export interface StoreAuditSummaryRangeAuditTotals {
  audit_count: number;
  total_sold: number;
  total_revenue: number;
  total_packs: number;
  total_games: number;
  newly_activated_count: number;
  newly_activated_pack_count: number;
  newly_activated_value: number;
  opening_count: number;
  opening_pack_count: number;
  closing_count: number;
  closing_pack_count: number;
  counter_sold_out_pack_count: number;
  moved_to_backstock_count: number;
  moved_to_backstock_ticket_count: number;
  opening_value: number;
  closing_value: number;
}

export interface StoreAuditSummaryRangePosTotals {
  scratcher_count: number;
  scratcher_sales: number;
  lotto_sales: number;
  payout: number;
}

export interface StoreAuditSummaryRangePosAuditWindowTotals {
  scratcher_count: number;
  scratcher_sales: number;
}

export interface StoreAuditSummaryRangeLottoTotals {
  sales: number;
  payouts_total: number;
  payouts_count: number;
  lotto_draw_sales_amount: number;
  lotto_voucher_issued_amount: number;
  lotto_gift_cards_issued_amount: number;
  lotto_draw_payouts_amount: number;
  lotto_voucher_redeemed_amount: number;
  lotto_card_trans_redeemed_amount: number;
  lotto_payouts_amount: number;
  scratcher_payouts_amount: number;
  activated_count: number;
  activated_pack_count: number;
  return_count: number;
  return_value_total: number;
  scratcher_free_tickets_paid_count: number;
  scratcher_free_tickets_paid_amount: number;
}

export interface StoreAuditSummaryRangeVarianceTotals {
  scratcher: {
    quantity_variance: number | null;
    revenue_variance: number | null;
  };
  lotto_sales_variance: number | null;
  payout_variance: number | null;
  total_variance: number | null;
}

export interface StoreAuditSummaryRangeTotals {
  audit: StoreAuditSummaryRangeAuditTotals | null;
  pos: StoreAuditSummaryRangePosTotals | null;
  pos_audit_window?: StoreAuditSummaryRangePosAuditWindowTotals | null;
  lotto: StoreAuditSummaryRangeLottoTotals | null;
  reconciliation_variance: StoreAuditSummaryRangeVarianceTotals | null;
}

export interface StoreAuditSummaryRangeVerified {
  is_verified?: boolean;
  audit_status?: string | null;
  date_time?: string | null;
  user_id?: string | null;
  user_name?: string | null;
  note?: string | null;
  status?: string | null;
  verified_at?: string | null;
  verified_by_user_name?: string | null;
  has_manager_note?: boolean;
  can_add_manager_note?: boolean;
  can_verify?: boolean;
}

export interface StoreAuditSummaryRangeDay {
  report_date: string;
  audits: unknown[];
  daily_totals: StoreAuditSummaryRangeTotals;
  subscription_status: unknown | null;
  verified: StoreAuditSummaryRangeVerified | null;
  audit_missing: boolean;
}

export interface StoreAuditSummaryRangeResponse {
  store_id: string;
  store_name?: string | null;
  store_timezone?: string | null;
  start_date: string;
  end_date: string;
  daily_summaries: StoreAuditSummaryRangeDay[];
  overall_totals: StoreAuditSummaryRangeTotals | null;
}

const parseNumber = (value: unknown): number => {
  if (value == null || value === "") {
    return 0;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const parseNullableNumber = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
};

const normalizeTotals = (totals: any): StoreAuditSummaryRangeTotals => {
  if (!totals) {
    return {
      audit: null,
      pos: null,
      pos_audit_window: null,
      lotto: null,
      reconciliation_variance: null,
    };
  }

  if (totals.audit || totals.pos || totals.pos_audit_window || totals.lotto || totals.reconciliation_variance) {
    const auditCount = parseNumber(totals.audit?.audit_count);
    const scratcherTotals = auditCount > 0 || !totals.pos
      ? totals.pos_audit_window
      : totals.pos;

    return {
      audit: totals.audit
        ? {
            audit_count: parseNumber(totals.audit.audit_count),
            total_sold: parseNumber(totals.audit.total_sold),
            total_revenue: parseNumber(totals.audit.total_revenue ?? totals.audit.revenue),
            total_packs: parseNumber(totals.audit.total_packs),
            total_games: parseNumber(totals.audit.total_games ?? totals.audit.games_count),
            newly_activated_count: parseNumber(totals.audit.newly_activated_count),
            newly_activated_pack_count: parseNumber(totals.audit.newly_activated_pack_count),
            newly_activated_value: parseNumber(totals.audit.newly_activated_value),
            opening_count: parseNumber(totals.audit.opening_count),
            opening_pack_count: parseNumber(totals.audit.opening_pack_count),
            closing_count: parseNumber(totals.audit.closing_count),
            closing_pack_count: parseNumber(totals.audit.closing_pack_count),
            counter_sold_out_pack_count: parseNumber(totals.audit.counter_sold_out_pack_count),
            moved_to_backstock_count: parseNumber(totals.audit.moved_to_backstock_count),
            moved_to_backstock_ticket_count: parseNumber(totals.audit.moved_to_backstock_ticket_count),
            opening_value: parseNumber(totals.audit.opening_value),
            closing_value: parseNumber(totals.audit.closing_value),
          }
        : null,
      pos: totals.pos || totals.pos_audit_window
        ? {
            scratcher_count: parseNumber(
              scratcherTotals?.scratcher_count ?? scratcherTotals?.scratcher_quantity_sold
            ),
            scratcher_sales: parseNumber(scratcherTotals?.scratcher_sales),
            lotto_sales: parseNumber(totals.pos?.lotto_sales),
            payout: parseNumber(totals.pos?.payout),
          }
        : null,
      pos_audit_window: totals.pos_audit_window
        ? {
            scratcher_count: parseNumber(totals.pos_audit_window.scratcher_count),
            scratcher_sales: parseNumber(totals.pos_audit_window.scratcher_sales),
          }
        : null,
      lotto: totals.lotto
        ? {
            sales: parseNumber(totals.lotto.sales),
            payouts_total: parseNumber(totals.lotto.payouts_total),
            payouts_count: parseNumber(totals.lotto.payouts_count),
            lotto_draw_sales_amount: parseNumber(totals.lotto.lotto_draw_sales_amount),
            lotto_voucher_issued_amount: parseNumber(totals.lotto.lotto_voucher_issued_amount),
            lotto_gift_cards_issued_amount: parseNumber(totals.lotto.lotto_gift_cards_issued_amount),
            lotto_draw_payouts_amount: parseNumber(totals.lotto.lotto_draw_payouts_amount),
            lotto_voucher_redeemed_amount: parseNumber(totals.lotto.lotto_voucher_redeemed_amount),
            lotto_card_trans_redeemed_amount: parseNumber(totals.lotto.lotto_card_trans_redeemed_amount),
            lotto_payouts_amount: parseNumber(totals.lotto.lotto_payouts_amount),
            scratcher_payouts_amount: parseNumber(totals.lotto.scratcher_payouts_amount),
            activated_count: parseNumber(totals.lotto.activated_count),
            activated_pack_count: parseNumber(totals.lotto.activated_pack_count),
            return_count: parseNumber(totals.lotto.return_count),
            return_value_total: parseNumber(totals.lotto.return_value_total),
            scratcher_free_tickets_paid_count: parseNumber(totals.lotto.scratcher_free_tickets_paid_count),
            scratcher_free_tickets_paid_amount: parseNumber(totals.lotto.scratcher_free_tickets_paid_amount),
          }
        : null,
      reconciliation_variance: totals.reconciliation_variance
        ? {
            scratcher: {
              quantity_variance: parseNullableNumber(totals.reconciliation_variance.scratcher?.quantity_variance),
              revenue_variance: parseNullableNumber(totals.reconciliation_variance.scratcher?.revenue_variance),
            },
            lotto_sales_variance: parseNullableNumber(totals.reconciliation_variance.lotto_sales_variance),
            payout_variance: parseNullableNumber(totals.reconciliation_variance.payout_variance),
            total_variance: parseNullableNumber(totals.reconciliation_variance.total_variance),
          }
        : null,
    };
  }

  return {
    audit: null,
    pos: null,
    pos_audit_window: null,
    lotto: null,
    reconciliation_variance: null,
  };
};

const normalizeCompatRow = (row: any) => ({
  report_date: row.report_date ?? row.reportDate ?? "",
  audits: Array.isArray(row.audits) ? row.audits : [],
  daily_totals: normalizeTotals(
    row.daily_totals ?? row.auditTotals ?? {
      audit: row.audit_summary
        ? {
            audit_count: row.audit_summary.audit_count,
            total_sold: row.audit_summary.total_sold,
            total_revenue: row.audit_summary.total_revenue ?? row.audit_summary.revenue,
            total_packs: row.audit_summary.total_packs,
            total_games: row.audit_summary.total_games ?? row.audit_summary.games_count,
            newly_activated_count: row.audit_summary.newly_activated_count,
            newly_activated_pack_count: row.audit_summary.newly_activated_pack_count,
            newly_activated_value: row.audit_summary.newly_activated_value,
            opening_count: row.audit_summary.opening_count,
            opening_pack_count: row.audit_summary.opening_pack_count,
            closing_count: row.audit_summary.closing_count,
            closing_pack_count: row.audit_summary.closing_pack_count,
            counter_sold_out_pack_count: row.audit_summary.counter_sold_out_pack_count,
            moved_to_backstock_count: row.audit_summary.moved_to_backstock_count,
            moved_to_backstock_ticket_count: row.audit_summary.moved_to_backstock_ticket_count,
            opening_value: row.audit_summary.opening_value,
            closing_value: row.audit_summary.closing_value,
          }
        : null,
      pos: row.pos_summary
        ? {
            scratcher_count: row.pos_summary.scratcher_count ?? row.pos_summary.scratcher_quantity_sold,
            scratcher_sales: row.pos_summary.scratcher_sales,
            lotto_sales: row.pos_summary.lotto_sales,
            payout: row.pos_summary.payout,
          }
        : null,
      pos_audit_window: row.pos_audit_window ?? row.posAuditWindow ?? null,
      lotto: row.lotto_summary
        ? {
            sales: row.lotto_summary.sales,
            payouts_total: row.lotto_summary.payouts_total,
            payouts_count: row.lotto_summary.payouts_count,
            lotto_draw_sales_amount: row.lotto_summary.lotto_draw_sales_amount,
            lotto_voucher_issued_amount: row.lotto_summary.lotto_voucher_issued_amount,
            lotto_gift_cards_issued_amount: row.lotto_summary.lotto_gift_cards_issued_amount,
            lotto_draw_payouts_amount: row.lotto_summary.lotto_draw_payouts_amount,
            lotto_voucher_redeemed_amount: row.lotto_summary.lotto_voucher_redeemed_amount,
            lotto_card_trans_redeemed_amount: row.lotto_summary.lotto_card_trans_redeemed_amount,
            lotto_payouts_amount: row.lotto_summary.lotto_payouts_amount,
            scratcher_payouts_amount: row.lotto_summary.scratcher_payouts_amount,
            activated_count: row.lotto_summary.activated_count,
            activated_pack_count: row.lotto_summary.activated_pack_count,
            return_count: row.lotto_summary.return_count,
            return_value_total: row.lotto_summary.return_value_total,
            scratcher_free_tickets_paid_count: row.lotto_summary.scratcher_free_tickets_paid_count,
            scratcher_free_tickets_paid_amount: row.lotto_summary.scratcher_free_tickets_paid_amount,
          }
        : null,
      reconciliation_variance: row.reconciliation_variance ?? row.auditTotals?.reconciliation_variance,
    }
  ),
  subscription_status: row.subscription_status ?? row.subscriptionStatus ?? null,
  verified: row.verified ?? null,
  audit_missing: Boolean(row.audit_missing ?? row.auditMissing),
});

export interface StoreAuditSummaryRangeDayModel {
  reportDate: string;
  audits: unknown[];
  auditTotals: StoreAuditSummaryRangeTotals;
  subscriptionStatus: unknown | null;
  verified: StoreAuditSummaryRangeVerified | null;
  auditMissing: boolean;
}

/**
 * Store Audit Summary Range Model
 * Mirrors the pattern used by DailyReportModel and keeps the API response behind getters.
 */
export default class StoreAuditSummaryRangeModel {
  private _serverData: StoreAuditSummaryRangeResponse;
  private _dailySummaries: StoreAuditSummaryRangeDayModel[] | null = null;

  constructor(data: StoreAuditSummaryRangeResponse) {
    this._serverData = data;
  }

  private _mapTotals(totals: StoreAuditSummaryRangeTotals | null): StoreAuditSummaryRangeTotals {
    if (!totals) {
      return {
        audit: null,
        pos: null,
        pos_audit_window: null,
        lotto: null,
        reconciliation_variance: null,
      };
    }

    const auditCount = parseNumber(totals.audit?.audit_count);
    const scratcherTotals = auditCount > 0 || !totals.pos
      ? totals.pos_audit_window
      : totals.pos;

    return {
      audit: totals.audit
        ? {
            audit_count: parseNumber(totals.audit.audit_count),
            total_sold: parseNumber(totals.audit.total_sold),
            total_revenue: parseNumber(totals.audit.total_revenue),
            total_packs: parseNumber(totals.audit.total_packs),
            total_games: parseNumber(totals.audit.total_games),
            newly_activated_count: parseNumber(totals.audit.newly_activated_count),
            newly_activated_pack_count: parseNumber(totals.audit.newly_activated_pack_count),
            newly_activated_value: parseNumber(totals.audit.newly_activated_value),
            opening_count: parseNumber(totals.audit.opening_count),
            opening_pack_count: parseNumber(totals.audit.opening_pack_count),
            closing_count: parseNumber(totals.audit.closing_count),
            closing_pack_count: parseNumber(totals.audit.closing_pack_count),
            counter_sold_out_pack_count: parseNumber(totals.audit.counter_sold_out_pack_count),
            moved_to_backstock_count: parseNumber(totals.audit.moved_to_backstock_count),
            moved_to_backstock_ticket_count: parseNumber(totals.audit.moved_to_backstock_ticket_count),
            opening_value: parseNumber(totals.audit.opening_value),
            closing_value: parseNumber(totals.audit.closing_value),
          }
        : null,
      pos: totals.pos || totals.pos_audit_window
        ? {
            scratcher_count: parseNumber(scratcherTotals?.scratcher_count),
            scratcher_sales: parseNumber(scratcherTotals?.scratcher_sales),
            lotto_sales: parseNumber(totals.pos?.lotto_sales),
            payout: parseNumber(totals.pos?.payout),
          }
        : null,
      pos_audit_window: totals.pos_audit_window
        ? {
            scratcher_count: parseNumber(totals.pos_audit_window.scratcher_count),
            scratcher_sales: parseNumber(totals.pos_audit_window.scratcher_sales),
          }
        : null,
      lotto: totals.lotto
        ? {
            sales: parseNumber(totals.lotto.sales),
            payouts_total: parseNumber(totals.lotto.payouts_total),
            payouts_count: parseNumber(totals.lotto.payouts_count),
            lotto_draw_sales_amount: parseNumber(totals.lotto.lotto_draw_sales_amount),
            lotto_voucher_issued_amount: parseNumber(totals.lotto.lotto_voucher_issued_amount),
            lotto_gift_cards_issued_amount: parseNumber(totals.lotto.lotto_gift_cards_issued_amount),
            lotto_draw_payouts_amount: parseNumber(totals.lotto.lotto_draw_payouts_amount),
            lotto_voucher_redeemed_amount: parseNumber(totals.lotto.lotto_voucher_redeemed_amount),
            lotto_card_trans_redeemed_amount: parseNumber(totals.lotto.lotto_card_trans_redeemed_amount),
            lotto_payouts_amount: parseNumber(totals.lotto.lotto_payouts_amount),
            scratcher_payouts_amount: parseNumber(totals.lotto.scratcher_payouts_amount),
            activated_count: parseNumber(totals.lotto.activated_count),
            activated_pack_count: parseNumber(totals.lotto.activated_pack_count),
            return_count: parseNumber(totals.lotto.return_count),
            return_value_total: parseNumber(totals.lotto.return_value_total),
            scratcher_free_tickets_paid_count: parseNumber(totals.lotto.scratcher_free_tickets_paid_count),
            scratcher_free_tickets_paid_amount: parseNumber(totals.lotto.scratcher_free_tickets_paid_amount),
          }
        : null,
      reconciliation_variance: totals.reconciliation_variance
        ? {
            scratcher: {
              quantity_variance: parseNullableNumber(totals.reconciliation_variance.scratcher.quantity_variance),
              revenue_variance: parseNullableNumber(totals.reconciliation_variance.scratcher.revenue_variance),
            },
            lotto_sales_variance: parseNullableNumber(totals.reconciliation_variance.lotto_sales_variance),
            payout_variance: parseNullableNumber(totals.reconciliation_variance.payout_variance),
            total_variance: parseNullableNumber(totals.reconciliation_variance.total_variance),
          }
        : null,
    };
  }

  private _mapDay(day: StoreAuditSummaryRangeDay): StoreAuditSummaryRangeDayModel {
    return {
      reportDate: String(day.report_date ?? ""),
      audits: Array.isArray(day.audits) ? day.audits : [],
      auditTotals: this._mapTotals(day.daily_totals),
      subscriptionStatus: day.subscription_status ?? null,
      verified: day.verified ?? null,
      auditMissing: Boolean(day.audit_missing),
    };
  }

  get storeId(): string {
    return this._serverData.store_id;
  }

  get storeName(): string | null {
    return this._serverData.store_name ?? null;
  }

  get storeTimezone(): string | null {
    return this._serverData.store_timezone ?? null;
  }

  get startDate(): string {
    return this._serverData.start_date;
  }

  get endDate(): string {
    return this._serverData.end_date;
  }

  get dailySummaries(): StoreAuditSummaryRangeDayModel[] {
    if (this._dailySummaries) {
      return this._dailySummaries;
    }

    this._dailySummaries = Array.isArray(this._serverData.daily_summaries)
      ? this._serverData.daily_summaries.map((day) => this._mapDay(day))
      : [];

    return this._dailySummaries;
  }

  get overallTotals(): StoreAuditSummaryRangeDayModel | null {
    if (!this._serverData.overall_totals) {
      return null;
    }

    return {
      reportDate: "overall_totals",
      audits: [],
      auditTotals: this._mapTotals(this._serverData.overall_totals),
      subscriptionStatus: null,
      verified: null,
      auditMissing: false,
    };
  }

  get dailySummaryCount(): number {
    return this.dailySummaries.length;
  }

  static fromServerData(data: any): StoreAuditSummaryRangeModel {
    if (data && Array.isArray(data.dailySummaries)) {
      return new StoreAuditSummaryRangeModel({
        store_id: data.storeId ?? "",
        store_name: data.storeName ?? null,
        store_timezone: data.storeTimezone ?? null,
        start_date: data.startDate ?? "",
        end_date: data.endDate ?? "",
        daily_summaries: Array.isArray(data.dailySummaries) ? data.dailySummaries.map(normalizeCompatRow) : [],
        overall_totals: data.overallTotals ? normalizeCompatRow(data.overallTotals).daily_totals : null,
      });
    }

    if (data && Array.isArray(data.daily_summaries)) {
      return new StoreAuditSummaryRangeModel(data as StoreAuditSummaryRangeResponse);
    }

    return new StoreAuditSummaryRangeModel({
      store_id: String(data?.store_id ?? data?.storeId ?? ""),
      store_name: data?.store_name ?? data?.storeName ?? null,
      store_timezone: data?.store_timezone ?? data?.storeTimezone ?? null,
      start_date: data?.start_date ?? data?.startDate ?? "",
      end_date: data?.end_date ?? data?.endDate ?? "",
      daily_summaries: Array.isArray(data?.dailySummaries)
        ? data.dailySummaries.map(normalizeCompatRow)
        : Array.isArray(data?.daily_summaries)
          ? data.daily_summaries.map((day: any) => ({
              report_date: day.report_date ?? day.reportDate ?? "",
              audits: Array.isArray(day.audits) ? day.audits : [],
              daily_totals: normalizeTotals(day.daily_totals ?? day.totals ?? null),
              subscription_status: day.subscription_status ?? day.subscriptionStatus ?? null,
              verified: day.verified ?? null,
              audit_missing: Boolean(day.audit_missing ?? day.auditMissing),
            }))
          : [],
      overall_totals: data?.overall_totals
        ? normalizeTotals(data.overall_totals)
        : data?.overallTotals
          ? normalizeCompatRow(data.overallTotals).daily_totals
          : null,
    });
  }
}
